<template>
  <Dialog
    width="560px"
    :title="$$t('create.melody')"
    :subTitle="$$t('create.add_melody')"
    :visible="renderData.visible"
    @close="closeDialog"
    :titleAlign="createRenderType === 'mobile' ? 'left' : 'center'"
    mobileRenderType="drawerDialog"
    :customClass="`edit-rocord-dialog--${createRenderType}`"
  >
    <div class="height-all edit-rocord flex-column">
      <div class="edit-rocord-lyrics">
        <template v-for="(item, index) in getLyrics()" :key="index">
          <div class="edit-rocord-lyrics-item">
            {{ item }}
          </div>
        </template>
      </div>
      <div class="edit-rocord-file" v-if="tools.isDev()">
        <ElementUpload ref="refUpload" :config="renderData.uploadRenderConfig">
          <div class="edit-rocord-file-box">seleteFile</div>
        </ElementUpload>
      </div>
      <div class="edit-rocord-record-box">
        <Record
          type="motif"
          ref="refRecord"
          @tooggleRecord="onTooggleRecord"
        ></Record>
      </div>
      <div class="edit-rocord-done-btn-box">
        <div class="edit-rocord-time">{{ $$t("create.motif_min_time") }}</div>
        <div class="edit-rocord-done-btn">
          <Button
            @click="onDoneClick"
            :disabled="renderData.disableDoneBtn"
            :renderStyle="{
              '--padding': '0',
              '--width': '100%',
              '--height': '48px',
              '--borderRadius': '50px',
              '--fontColor': '#000',
              '--fontSize': '16px',
              '--fontWeight': 'normal',
              '--border': 'none',
              '--background':
                'linear-gradient(120deg, rgb(79, 222, 255, 1) -1%, rgb(230, 117, 255, 1) 100%)', //#94ADFF
              '--backgroundHover':
                'linear-gradient(120deg, rgb(79, 222, 255, 0.75) -1%, rgb(230, 117, 255, 0.75) 100%)',
              '--fontColorHover': '#000',
            }"
          >
            {{ $$t("create.done") }}
          </Button>
        </div>
      </div>
    </div>
  </Dialog>
</template>

<script setup>
import {
  ref,
  defineProps,
  defineEmits,
  computed,
  readonly,
  reactive,
  watch,
  defineExpose,
  onMounted,
  onUpdated,
  onUnmounted,
} from "vue";

import Dialog from "@/components/basic/dialog/Dialog.vue";
import Button from "@/components/basic/button/Button.vue";
import Record from "@/components/media/record/Record.vue";
import message from "@/components/functionCallComponent/message.jsx";
import clipUtil from "@/components/media/utils/clipUtil.js";
import ffmpegUtil from "@/components/media/utils/ffmpegUtil.js";

import ElementUpload from "@/components/basic/upload/elementUpload.jsx";

import axios from "axios";
import tools from "@/utils/tools.js";
import objectStorage, {
  getObjectStorageFullUrl,
} from "@/lib/objectStorage/objectStorage.js";
import apiReferMusic from "@/api/api/apiReferMusic.js";

import useDraftDataStore from "@/store/create/draftDataStore.js";
import useMotifDataStore from "@/store/create/motifDataStore.js";

import useLyricDataStore from "@/store/create/lyricDataStore.js";
const lyricDataStore = useLyricDataStore();
const motifDataStore = useMotifDataStore();
const draftDataStore = useDraftDataStore();

import { storeToRefs } from "pinia";
import useCreateLayoutStore from "@/store/create/createLayoutStore.js";
const createLayoutStore = useCreateLayoutStore();
const { createRenderType } = storeToRefs(createLayoutStore);
const { $$language, $$t, $$tm, $$tValue } = getCurrentInstance().appContext?.config.globalProperties.$i18n ?? {}

const acceptedFileTypes = [
  "audio/x-wav",
 "video/quicktime",
  "video/mp4",
  "audio/wav",
  "audio/x-m4a",
  "audio/flac",
  "audio/mpeg",
  ".mp3",
  ".wav",
  ".m4a",
  ".mp4",
  ".mov",
  ".MP4",
  ".flac",
];
const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
});

const emits = defineEmits(["update:visible", "apply"]);
const refRecord = ref(null);
const refUpload = ref(null);
const renderData = reactive({
  visible: props.visible,
  disableDoneBtn: false,
  uploadRenderConfig: {
    accept: acceptedFileTypes.join(","),
    multiple: false,
    drag: true,
    onChange: async (uploadFile, uploadFiles) => {
      if (uploadFile?.raw) {
        const file = uploadFile.raw;
        if (file.size > 1024 * 1024 * 100) {
          message.info({
            content: $$t("create.max_file_size"),
            duration: 1500,
          });
          refUpload.value?.clearInput();
          return;
        }
        try {
          const duration = await tools.getMediaFileDuration(file);
          if (duration > 0) {
            renderData.duration = duration;
            renderData.linkurl = "";
            renderData.file = file;
          } else {
            refUpload.value?.clearInput();
            message.info({
              position: "top",
              content: $$t("create.select_audio_or_video"),
            });
            return;
          }
        } catch (e) {
          refUpload.value?.clearInput();
          message.info({
            position: "top",
            content: $$t("create.select_audio_or_video"),
          });
          return;
        }
        renderData.recoderData = {
          file: file,
          duration: renderData.duration,
        };
        handleEditFileCompleted(renderData.recoderData.file);
      }
    },
    autofalse: false,
  },
});

const refRecorder = ref();
const getLyrics = () => {
  return lyricDataStore.lyric?.split("\n") ?? [];
};
const closeDialog = () => {
  renderData.visible = false;
};

const onTooggleRecord = (isRecoder) => {
  renderData.disableDoneBtn = isRecoder;
};

const onDoneClick = async () => {
  renderData.recoderData = await refRecord.value.getRecord();
  if (renderData.recoderData?.duration < 5) {
    message.info({
      position: "top",
      content: $$t("create.motif_min_time_tip"),
    });
  } else {
    handleEditFileCompleted(renderData.recoderData.file);
    // motifDataStore.setMotifData(renderData.recoderData);
  }
};

const handleEditFileCompleted = async (fileData) => {
  const closeMsg = message.loading({
    position: "center",
    content: $$t("create.saving"),
  });
  let saveData = null;
  try {
    const objectStorageRes = await objectStorage.uploadFileToObjectStorage(
      fileData,
      (obj) => {
        renderData.uploadCallbackData = obj;
      }
    );

    saveData = {
      // title: fileSaveData.filename,
      url: objectStorageRes.pathKey,
      duration_milliseconds: Math.ceil(renderData.recoderData?.duration * 1000),
      feed_id: draftDataStore.feed_id,
      bpm: 0,
    };
  } catch (e) {
    closeMsg.clear();
    console.error("uploadFileToObjectStorage error", e);
    message.error({
      position: "top",
      content: $$t("create.save_failed"),
    });
    return;
  }

  let cancelToken = axios.CancelToken.source();
  apiReferMusic
    .addMotifMusic(saveData, { cancelToken: cancelToken.token })
    .then((res) => {
      if (res.code == 200 && res.data) {
        closeMsg.clear();
        motifDataStore.setMotifData(res.data);
        emits("apply");
        closeDialog();
      } else {
        if (res.code == 6308) {
          message.error({
            position: "top",
            content: $$t("create.motif_min_time_tip"),
          });
        } else {
          message.error({
            position: "top",
            content: $$t("create.save_failed"),
          });
        }
      }
    })
    .catch((err) => {
      console.error("addReferenceMusic error", err);
      message.error({
        position: "top",
        content: $$t("create.save_failed"),
      });
    });
};

watch(
  () => props.visible,
  (val) => {
    renderData.visible = props.visible;
  }
);

watch(
  () => renderData.visible,
  (val) => {
    emits("update:visible", val);
  }
);

const onTabClick = () => {};
onMounted(() => {});
</script>
<style lang="scss" scope>
.edit-rocord {
  margin: 15px 0 32px 0;
  .edit-rocord-lyrics {
    font-family: HarmonyOS Sans SC;
    font-size: 14px;
    font-weight: normal;
    line-height: 24px;
    text-align: center;
    color: #ffffff;
    margin: 0 70px 14px 70px;
    max-height: 180px;
    overflow-y: scroll;
  }
  .edit-rocord-file {
    height: 40px;
  }
  .el-upload-dragger {
    border: none;
    border-radius: 40px;
    align-items: center;
    .edit-rocord-file-box {
      border-radius: 40px;
      height: 100%;
      font-family: HarmonyOS Sans SC;
      font-size: 14px;
      font-weight: normal;
      line-height: 40px;
      text-align: center;
      color: #ffffff;
    }
    &.is-dragover {
      border-radius: 40px;
      border: 1px dashed #94adff;
    }
    .draging-dashed {
      display: none;
    }
  }
  .el-upload__input {
    height: 0;
  }
}
.edit-rocord-done-btn-box {
  margin: 12px 32px 0 32px;
  .edit-rocord-time {
    font-family: HarmonyOS Sans SC;
    font-size: 12px;
    font-weight: normal;
    margin-bottom: 20px;
    text-align: center;
    color: rgba(255, 255, 255, 0.5);
    margin-bottom: 20px;
    text-align: center;
    color: rgba(255, 255, 255, 0.5);
  }
  .edit-rocord-done-btn {
    width: 400px;
    max-width: 100%;
    margin: 0 auto;
  }
}
</style>
